import React, { useEffect } from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock, CodeEditor, BoldTextParagraph,   InlineCode,   SectionTitle,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider,  MutedText, Separator, CorrectionBox, Result} from '../../../../components/course/CourseComponents';

const Chapitre6Content = ({ courseId = 'baseprogrammation' }) => {
  
  // Effet pour donner le focus à l'éditeur quand le composant se monte
  useEffect(() => {
    // Le focus sera géré par la prop autoFocus du CodeEditor
  }, []);

  return (
    <>
     <InfoBox title="Objectif" color="blue">
        <p className="mb-0">
        Regrouper plusieurs données ordonnées dans une seule variable.
        </p>
      </InfoBox>       

      <Separator type="text" text="Qu'est-ce qu'une liste ?" color="gray-darker" size="md" />
      <SectionTitle>Qu'est-ce qu'une liste ?</SectionTitle>
      <p>
        Une liste est une boîte qui contient plusieurs valeurs, dans un certain ordre. 
        C'est très utile pour :
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>stocker des objets du jeu</li>
        <li>gérer un inventaire</li>
        <li>enregistrer des scores</li>
      </ul>

      <Separator type="text" text="Créer une liste" color="gray-darker" size="md" />
      <SectionTitle>Créer une liste</SectionTitle>

      <Note type="gray" title="📦 Créer une liste :">
        <CodeBlock language="javascript">
{`let fruits = ["pomme", "banane", "fraise"];`}
        </CodeBlock>
        <p  >👉 Ici, la liste <InlineCode>fruits</InlineCode> contient 3 éléments.</p>
      </Note>

      <p>Tu peux accéder aux éléments comme ceci :</p>

      <Note type="gray" title="🔍 Accéder aux éléments :">
        <CodeBlock language="javascript">
{`print(fruits[0]); // "pomme"
print(fruits[2]); // "fraise"`}
        </CodeBlock>
      </Note>

      <Note type="info" title="💡 Important :">
        En JavaScript, les indices commencent à 0 ! Le premier élément est à l'index 0, le deuxième à l'index 1, etc.
      </Note>

      <Separator type="text" text="Fonctions simple" color="gray-darker" size="md" />
      <SectionTitle>Les fonctions simples pour manipuler une liste</SectionTitle>
      <Note type="" title="✏️ Modifier un élément :">
        <CodeBlock language="javascript">
{`fruits[1] = "kiwi"; // remplace "banane" par "kiwi"`}
        </CodeBlock>
      </Note>
      <Note type="" title="➕ Ajouter des éléments :">
        <CodeBlock language="javascript">
{`fruits.push("orange");   // ajoute à la fin
fruits.unshift("ananas"); // ajoute au début`}
        </CodeBlock>
      </Note>

      <Note type="" title="➖ Supprimer des éléments :">
        <CodeBlock language="javascript">
{`fruits.pop();    // enlève le dernier
fruits.shift();  // enlève le premier
fruits.splice(i, 1);  // supprime l'élément à l'index i`}
        </CodeBlock>
      </Note>

      <Note type="" title="📏 Taille de la liste :">
        <CodeBlock language="javascript">
{`print(fruits.length); // affiche le nombre d'éléments`}
        </CodeBlock>
      </Note>

      <Separator type="text" text="Parcourir une liste" color="gray-darker" size="md" />
      <SectionTitle>Parcourir une liste avec for</SectionTitle>

      <Note type="gray" title="🔁 Parcourir une liste :">
        <CodeBlock language="javascript">
{`for (let i = 0; i < fruits.length; i++) {
  print(fruits[i]);
}`}
        </CodeBlock>
        <p  >👉 Affiche chaque fruit de la liste</p>
      </Note>

      <Note type="exercice" title="🧪 Exercice listes :">
        Crée une liste de 3 noms de jeux vidéo. Affiche chacun d'eux dans la console avec une boucle for :<br/>
        - Crée une liste <InlineCode>jeux</InlineCode> avec 3 jeux<br/>
        - Utilise une boucle <InlineCode>for</InlineCode> pour parcourir<br/>
        - Affiche chaque jeu avec <InlineCode>print()</InlineCode><br/>
        <Result>Minecraft | Zelda | Fortnite</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let jeux = ["Minecraft", "Zelda", "Fortnite"];
for (let i = 0; i < jeux.length; i++) {
  print(jeux[i]);
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="listes-base"
        title="Exercice : Créer et parcourir une liste"
        showConsole={true}
        height="250px"
        initialCode=""
        autoFocus={true}
      />

      <Separator type="text" text="Fonctions avancées" color="gray-darker" size="md" />
      <SectionTitle>Fonctions avancées pour manipuler une liste</SectionTitle>
      

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>includes(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Vérifie si x est dans la liste</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>indexOf(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Donne la position de x</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>join(", ")</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Transforme la liste en texte</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>reverse()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Inverse l'ordre des éléments</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>sort()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Trie la liste</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Separator type="text" text="Tableaux à 2 dimensions" color="gray-darker" size="md" />
      <SectionTitle>Les tableaux à deux dimensions</SectionTitle>
      <p>
        Un tableau à 2 dimensions, c'est une liste de listes. Parfait pour créer :
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>des grilles de jeu</li>
        <li>des cartes</li>
        <li>des tableaux de scores</li>
      </ul>

      <Note type="gray" title="📦 Exemple de tableau 2D :">
        <CodeBlock language="javascript">
{`let grille = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9]
];`}
        </CodeBlock>
        <p  >👉 Ici, <InlineCode>grille[0][1]</InlineCode> donne 2</p>
      </Note>

      <Note type="gray" title="🔍 Lire et modifier :">
        <CodeBlock language="javascript">
{`print(grille[1][2]); // affiche 6

// Modifier une case
grille[2][0] = 99;`}
        </CodeBlock>
      </Note>

      <SubHeading>Parcourir une grille</SubHeading>
      <p>On utilise deux boucles : une pour les lignes, une pour les colonnes :<br/>
      au début, ligne vaut 0, et la deuxième boucle for permet de parcourir toutes les colonnes de la ligne 0. Ensuite, ligne vaut 1, et on parcourt toutes les colonnes de la ligne 1.<br/>
      Ensuite, ligne vaut 2, et on parcourt toutes les colonnes de la ligne 2.<br/>
      Et ainsi de suite jusqu'à la dernière ligne.

      </p>

      <Note type="gray" title="🔁 Parcourir une grille :">
        <CodeBlock language="javascript">
{`for (let ligne = 0; ligne < grille.length; ligne++) {
  for (let colonne = 0; colonne < grille[ligne].length; colonne++) {
    print(grille[ligne][colonne]);
  }
}`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 Exercice tableau 2D :">
        Crée une grille 3x3 contenant que des 0 ou des 1. Puis affiche le nombre de 1 present dans le tableau :<br/>
        - Crée une grille <InlineCode>grille</InlineCode> contenant des 1 et des 0<br/>
        - Crée une variable <InlineCode>compteurUn</InlineCode> initialisée à 0<br/>
        - Utilise 2 boucles <InlineCode>for</InlineCode> pour parcourir chaque ligne et chaque colonne<br/>
        - Si la case vaut <InlineCode>1</InlineCode>, ajoute 1 à <InlineCode>compteurUn</InlineCode><br/>
        - À la fin, affiche <InlineCode>compteurUn</InlineCode> avec <InlineCode>print()</InlineCode><br/>
        <Result>Nombre de 1 : ...</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let carte = [
  [1, 0, 0],
  [0, 1, 0],
  [1, 1, 1]
];

let compteurUn = 0;
for (let i = 0; i < grille.length; i++) {
  for (let j = 0; j < grille[i].length; j++) {
    if (grille[i][j] === 1) {
      compteurUn++;
    }
  }
}

print("Nombre de 1 :", compteurUn);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="grilles-2d"
        title="Exercice tableau 2D"
        showConsole={true}
        height="300px"
        initialCode=""
      />

      <p>
        ✅ Bravo ! Tu peux maintenant gérer des structures plus complexes comme une carte de jeu 🗺️
      </p>
      
      <BoldTextParagraph>
        Prochaine étape : les structures (objets) !
      </BoldTextParagraph>

  </>
  );
};

export default Chapitre6Content; 